import { createContext, useCallback, useContext, useMemo } from 'react';

import { RefetchOptions, useQuery, UseQueryResult } from '@tanstack/react-query';

import { useActingClientAuthorizedCheck } from 'components/patient/useActingClientAuthorizedCheck';
import { useEpisodeId } from 'hooks/useEpisodeId';
import useInvalidateQueries, { InvalidateOptions } from 'hooks/useInvalidateQueries';
import Episode from 'models/Episode';
import { episodeQueryKeys, showEpisode } from 'services/api/episode';

type EpisodeContextType = {
  episode?: Episode;
  loading: boolean;
  invalidateData: (options?: InvalidateOptions) => void;
  refetch: (options?: RefetchOptions) => Promise<UseQueryResult<Episode, Error>>;
};

const EpisodeContext = createContext<EpisodeContextType>({
  episode: {} as Episode,
  loading: true,
  invalidateData: () => {},
  refetch: (_options?: RefetchOptions) => Promise.resolve({} as UseQueryResult<Episode, Error>),
});

export function EpisodeProvider({ children }: { children: React.ReactNode }) {
  const episodeId = useEpisodeId();

  const invalidateQueries = useInvalidateQueries();

  const episodeQueryParams = useMemo(
    () => ({
      id: episodeId ?? '',
      include: [
        'episode_groups.group.group_type',
        'latest_location_episode.assigned_users.credential',
        'latest_location_episode_discharge',
        'latest_location_episode.group',
        'latest_location_episode.group.group_type',
        'owner.client.enabled_flags',
        'owner.client.enabled_provider_types',
        'owner.group_type',
        'transferred_from.group_type',
      ].join(','),
    }),
    [episodeId]
  );

  const authorizationChecked = useActingClientAuthorizedCheck(episodeId);

  const enabled = authorizationChecked && !!episodeId;

  const { data, isPending, isRefetching, refetch } = useQuery({
    queryKey: episodeQueryKeys.show(episodeQueryParams),
    queryFn: () => showEpisode(episodeQueryParams),
    enabled,
  });

  const invalidateData = useCallback(
    (options?: InvalidateOptions) => {
      invalidateQueries({
        episode: episodeQueryParams,
        ...options,
      });
    },
    [episodeQueryParams, invalidateQueries]
  );

  const episodeContext = useMemo(
    () => ({
      episode: enabled ? data : undefined,
      loading: isPending || isRefetching,
      invalidateData,
      refetch,
    }),
    [data, isPending, isRefetching, invalidateData, refetch, enabled]
  );

  return <EpisodeContext value={episodeContext}>{children}</EpisodeContext>;
}

export function useEpisode() {
  const context = useContext(EpisodeContext);

  if (!context) {
    throw new Error('useEpisode must be used within an EpisodeProvider');
  }

  return context;
}
